import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaBox, FaPrint, FaShippingFast, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  z-index: 1000;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h3`
  color: #333;
  margin-top: 0;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    color: #C41E3A;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #C41E3A;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #A01930;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #f1f3f4;
  color: #333;
  margin-right: 1rem;
  
  &:hover:not(:disabled) {
    background-color: #e2e3e5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  color: #333;
  margin-top: 0;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
`;

const RateCard = styled.div`
  border: 1px solid ${props => props.selected ? '#C41E3A' : '#ddd'};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  background-color: ${props => props.selected ? 'rgba(196, 30, 58, 0.05)' : 'white'};
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.selected ? '#C41E3A' : '#aaa'};
  }
`;

const RateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const RateCarrier = styled.div`
  font-weight: 600;
  color: #333;
`;

const RatePrice = styled.div`
  font-weight: 600;
  color: ${props => props.selected ? '#C41E3A' : '#333'};
`;

const RateDetails = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const RatesContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
`;

const ErrorMessage = styled.div`
  color: #C41E3A;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: rgba(196, 30, 58, 0.1);
  border-radius: 4px;
`;

const SuccessMessage = styled.div`
  color: #0f5132;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #d1e7dd;
  border-radius: 4px;
`;

const LabelPreview = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  
  img {
    max-width: 100%;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 8px;
`;

const SpinnerIcon = styled(FaSpinner)`
  animation: spin 1s linear infinite;
  font-size: 2rem;
  color: #C41E3A;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ShippingLabelCreator = ({ order, onClose, onLabelCreated }) => {
  const [step, setStep] = useState('dimensions');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Get default settings from Firestore
  const [storeSettings, setStoreSettings] = useState(null);
  
  // Add flags to control address editing
  const [editingFromAddress, setEditingFromAddress] = useState(false);
  const [editingToAddress, setEditingToAddress] = useState(false);
  
  const [parcel, setParcel] = useState({
    length: '8',
    width: '6',
    height: '4',
    distance_unit: 'in',
    weight: '1',
    mass_unit: 'lb'
  });
  
  const [fromAddress, setFromAddress] = useState({
    name: '',
    street1: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    phone: '',
    email: ''
  });
  
  const [toAddress, setToAddress] = useState({
    name: '',
    street1: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    phone: '',
    email: ''
  });
  
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [label, setLabel] = useState(null);
  
  // Fetch store settings on component mount
  useEffect(() => {
    console.log("ShippingLabelCreator mounted with order:", order);
    
    const fetchSettings = async () => {
      try {
        const settingsDoc = doc(db, 'settings', 'storeSettings');
        const settingsSnapshot = await getDoc(settingsDoc);
        
        if (settingsSnapshot.exists()) {
          const settings = settingsSnapshot.data();
          console.log("Settings loaded:", settings);
          setStoreSettings(settings);
          
          // Pre-fill from address if default address is set
          if (settings.defaultFromAddress) {
            setFromAddress(settings.defaultFromAddress);
          } else {
            console.log("No defaultFromAddress found in settings, using fallback values");
            // Use fallback values if no default address is set
            setFromAddress({
              name: settings.storeName || 'Master Collectors Forge',
              street1: settings.storeAddress || '123 Main St',
              city: 'Anytown',
              state: 'CA',
              zip: '90210',
              country: 'US',
              phone: settings.storePhone || '555-123-4567',
              email: settings.storeEmail || 'shipping@mastercollectorsforge.com'
            });
          }
        } else {
          console.log("No settings document found in Firestore, using fallback values");
          // Use fallback values if no settings document exists
          setFromAddress({
            name: 'Master Collectors Forge',
            street1: '123 Main St',
            city: 'Anytown',
            state: 'CA',
            zip: '90210',
            country: 'US',
            phone: '555-123-4567',
            email: 'shipping@mastercollectorsforge.com'
          });
        }
      } catch (error) {
        console.error('Error fetching store settings:', error);
        // Continue with default values instead of failing
        console.log("Error fetching settings, using fallback values");
        setFromAddress({
          name: 'Master Collectors Forge',
          street1: '123 Main St',
          city: 'Anytown',
          state: 'CA',
          zip: '90210',
          country: 'US',
          phone: '555-123-4567',
          email: 'shipping@mastercollectorsforge.com'
        });
      }
    };
    
    fetchSettings();
  }, [order]);
  
  useEffect(() => {
    if (order && order.customerInfo) {
      setToAddress({
        name: order.customer || order.customerInfo.name || '',
        street1: order.customerInfo.address || '',
        city: order.customerInfo.city || '',
        state: order.customerInfo.state || '',
        zip: order.customerInfo.zipCode || '',
        country: order.customerInfo.country || 'US',
        phone: order.customerInfo.phone || '',
        email: order.email || ''
      });
      console.log("Updated recipient address from order data");
    }
  }, [order]);
  
  // Store form data
  const handleFromAddressChange = (e) => {
    const { name, value } = e.target;
    setFromAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleToAddressChange = (e) => {
    const { name, value } = e.target;
    setToAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleParcelChange = (e) => {
    const { name, value } = e.target;
    setParcel(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateDimensions = () => {
    console.log("Validating dimensions:", parcel);
    if (!parcel.length || !parcel.width || !parcel.height || !parcel.weight) {
      setError('Please fill in all package dimensions and weight');
      console.log("Dimension validation failed: missing values");
      return false;
    }
    
    if (isNaN(parcel.length) || isNaN(parcel.width) || isNaN(parcel.height) || isNaN(parcel.weight)) {
      setError('Dimensions and weight must be numbers');
      console.log("Dimension validation failed: not numbers");
      return false;
    }
    
    console.log("Dimension validation passed");
    return true;
  };
  
  const validateToAddress = () => {
    console.log("Validating recipient address:", toAddress);
    
    // Check if all required fields are present
    if (!toAddress.name || !toAddress.street1 || !toAddress.city || 
        !toAddress.state || !toAddress.zip || !toAddress.country) {
      setError('Please fill in all required fields for the recipient address');
      console.log("Recipient address validation failed: missing values");
      return false;
    }
    
    // Validate US zip code format (5 digits or 5+4 format)
    if (toAddress.country === 'US') {
      // First, remove any non-digit characters
      const cleanZip = toAddress.zip.replace(/\D/g, '');
      
      // Check if we have at least 5 digits
      if (cleanZip.length < 5) {
        setError('Please enter a valid US ZIP code (at least 5 digits)');
        console.log("Recipient address validation failed: ZIP code too short");
        return false;
      }
      
      // Update the ZIP code to ensure it's properly formatted (first 5 digits only)
      setToAddress(prev => ({
        ...prev,
        zip: cleanZip.slice(0, 5)
      }));
      
      console.log("Updated recipient ZIP code to:", cleanZip.slice(0, 5));
    }
    
    console.log("Recipient address validation passed");
    return true;
  };

  const validateFromAddress = () => {
    console.log("Validating sender address:", fromAddress);
    
    // Check if all required fields are present
    if (!fromAddress.name || !fromAddress.street1 || !fromAddress.city || 
        !fromAddress.state || !fromAddress.zip || !fromAddress.country) {
      setError('Please fill in all required fields for the sender address');
      console.log("Sender address validation failed: missing values");
      return false;
    }
    
    // Validate US zip code format (5 digits or 5+4 format)
    if (fromAddress.country === 'US') {
      // First, remove any non-digit characters
      const cleanZip = fromAddress.zip.replace(/\D/g, '');
      
      // Check if we have at least 5 digits
      if (cleanZip.length < 5) {
        setError('Please enter a valid US ZIP code (at least 5 digits)');
        console.log("Sender address validation failed: ZIP code too short");
        return false;
      }
      
      // Update the ZIP code to ensure it's properly formatted (first 5 digits only)
      setFromAddress(prev => ({
        ...prev,
        zip: cleanZip.slice(0, 5)
      }));
      
      console.log("Updated sender ZIP code to:", cleanZip.slice(0, 5));
    }
    
    console.log("Sender address validation passed");
    return true;
  };
  
  const handleGetRates = async () => {
    console.log("Getting shipping rates...");
    console.log("Current parcel data:", parcel);
    console.log("Current fromAddress data:", fromAddress);
    console.log("Current toAddress data:", toAddress);
    
    // Validate dimensions and addresses
    if (!validateDimensions() || !validateFromAddress() || !validateToAddress()) {
      console.log("Validation failed");
      return;
    }
    
    setError('');
    setLoading(true);
    
    const requestData = {
      orderId: order?.id,
      fromAddress,
      toAddress,
      parcel
    };
    
    console.log("Sending request to Netlify function:", requestData);
    
    try {
      // Use the full Netlify URL
      const netlifyUrl = 'https://benevolent-sfogliatella-83141e.netlify.app/.netlify/functions/create-shipping-label';
      console.log("Fetching from URL:", netlifyUrl);
      
      const response = await fetch(netlifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(`Error fetching shipping rates: ${response.status} ${errorText}`);
      }
      
      // Try to parse the response as JSON, but handle non-JSON responses
      let responseText = await response.text();
      console.log("Raw response (first 500 chars):", responseText.substring(0, 500));
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        throw new Error(`Invalid response from server. This usually indicates the Netlify function is not being called correctly. Please check the Netlify function URL and deployment.`);
      }
      
      console.log("Shipping rates result:", result);
      
      if (result.rates && result.rates.length > 0) {
        // Remove duplicate rates based on object_id
        const uniqueRates = result.rates.filter((rate, index, self) =>
          index === self.findIndex((r) => r.object_id === rate.object_id)
        );
        
        console.log(`Original rates count: ${result.rates.length}, Unique rates count: ${uniqueRates.length}`);
        
        // Sort rates by price
        const sortedRates = uniqueRates.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
        setRates(sortedRates);
        setSelectedRate(sortedRates[0]); // Select cheapest rate by default
        setStep('rates');
      } else {
        throw new Error('No shipping rates available for this package and destination');
      }
      
    } catch (error) {
      console.error('Error getting shipping rates:', error);
      setError(error.message || 'Failed to get shipping rates');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateLabel = async () => {
    if (!selectedRate) {
      setError('Please select a shipping rate');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      // Use the full Netlify URL
      const netlifyUrl = 'https://benevolent-sfogliatella-83141e.netlify.app/.netlify/functions/create-shipping-label';
      console.log("Creating label using URL:", netlifyUrl);
      
      // Final validation and formatting of addresses
      // Ensure all required fields are present and properly formatted
      const formattedFromAddress = {
        name: fromAddress.name,
        street1: fromAddress.street1,
        city: fromAddress.city,
        state: fromAddress.state,
        zip: String(fromAddress.zip || '').replace(/\D/g, '').slice(0, 5),
        country: fromAddress.country,
        phone: fromAddress.phone,
        email: fromAddress.email
      };
      
      const formattedToAddress = {
        name: toAddress.name,
        street1: toAddress.street1,
        city: toAddress.city,
        state: toAddress.state,
        zip: String(toAddress.zip || '').replace(/\D/g, '').slice(0, 5),
        country: toAddress.country,
        phone: toAddress.phone,
        email: toAddress.email
      };
      
      // Use the exact same format as the test version that worked
      const requestData = {
        orderId: order.id,
        fromAddress: formattedFromAddress,
        toAddress: formattedToAddress,
        parcel: {
          length: String(parcel.length),
          width: String(parcel.width),
          height: String(parcel.height),
          distance_unit: parcel.distance_unit,
          weight: String(parcel.weight),
          mass_unit: parcel.mass_unit
        },
        // Use the rate object directly instead of just the carrier account and service level
        selectedRate: selectedRate
      };
      
      console.log("Sending request with formatted data:", JSON.stringify(requestData, null, 2));
      
      // Send a single request with all the necessary data
      // The Netlify function will handle both creating the shipment and the transaction
      const response = await fetch(netlifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(`Error creating shipping label: ${response.status} ${errorText}`);
      }
      
      // Try to parse the response as JSON, but handle non-JSON responses
      let responseText = await response.text();
      console.log("Raw response (first 500 chars):", responseText.substring(0, 500));
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        throw new Error(`Invalid response from server. This usually indicates the Netlify function is not being called correctly. Please check the Netlify function URL and deployment.`);
      }
      
      console.log("Full label creation result:", JSON.stringify(result, null, 2));
      
      // Check if we got a label in the response
      if (result.label && result.label.status === 'SUCCESS') {
        setLabel(result.label);
        setStep('label');
        setSuccess('Shipping label created successfully!');
        
        // Call the callback to update the order with tracking information
        if (onLabelCreated) {
          onLabelCreated({
            trackingNumber: result.label.tracking_number,
            carrier: selectedRate?.provider,
            labelUrl: result.label.label_url,
            trackingUrl: result.label.tracking_url_provider
          });
        }
      } else {
        // If no label was created, show detailed error information
        console.log("Label creation failed. Response:", result);
        
        // Check if we at least got rates back, which means the shipment was created successfully
        if (result.rates && result.rates.length > 0) {
          console.log("Shipment was created successfully with rates, but label creation failed");
          
          // Try again with a different approach - send the user back to the rates screen
          setRates(result.rates);
          setSelectedRate(result.rates[0]); // Select cheapest rate by default
          setStep('rates');
          setError('There was an issue creating the label. Please try selecting a different shipping rate.');
          return;
        }
        
        let errorMessage = 'Failed to create shipping label. ';
        
        if (result.label) {
          if (result.label.status) {
            errorMessage += `Status: ${result.label.status}. `;
          }
          
          if (result.label.messages && result.label.messages.length > 0) {
            errorMessage += `Errors: ${result.label.messages.map(msg => `${msg.source}: ${msg.text}`).join(', ')}`;
          }
        } else if (result.shipment && result.shipment.messages) {
          errorMessage += `Shipment errors: ${result.shipment.messages.map(msg => `${msg.source}: ${msg.text}`).join(', ')}`;
        } else if (result.error) {
          errorMessage += result.error;
        } else {
          errorMessage += 'The response did not contain a valid label or error information.';
        }
        
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('Error creating shipping label:', error);
      setError(error.message || 'Failed to create shipping label');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrintLabel = () => {
    if (label && label.label_url) {
      window.open(label.label_url, '_blank');
    }
  };
  
  return (
    <Container style={{ position: 'relative' }}>
      {loading && (
        <LoadingOverlay>
          <SpinnerIcon />
          <div>Processing your request...</div>
        </LoadingOverlay>
      )}
      
      <ContentWrapper>
        <Title>
          <FaShippingFast /> Create Shipping Label
        </Title>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        {step === 'dimensions' && (
          <>
            <Grid>
              <Section>
                <SectionTitle>Package Dimensions</SectionTitle>
                
                <Grid>
                  <FormGroup>
                    <Label htmlFor="length">Length (inches)</Label>
                    <Input
                      type="number"
                      id="length"
                      name="length"
                      value={parcel.length}
                      onChange={handleParcelChange}
                      min="0.1"
                      step="0.1"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="width">Width (inches)</Label>
                    <Input
                      type="number"
                      id="width"
                      name="width"
                      value={parcel.width}
                      onChange={handleParcelChange}
                      min="0.1"
                      step="0.1"
                      required
                    />
                  </FormGroup>
                </Grid>
                
                <Grid>
                  <FormGroup>
                    <Label htmlFor="height">Height (inches)</Label>
                    <Input
                      type="number"
                      id="height"
                      name="height"
                      value={parcel.height}
                      onChange={handleParcelChange}
                      min="0.1"
                      step="0.1"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="weight">Weight (ounces)</Label>
                    <Input
                      type="number"
                      id="weight"
                      name="weight"
                      value={parcel.weight}
                      onChange={handleParcelChange}
                      min="0.1"
                      step="0.1"
                      required
                    />
                  </FormGroup>
                </Grid>
              </Section>
              
              <Section>
                <SectionTitle>Sender Address</SectionTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div>Where the package will be shipped from</div>
                  <Button 
                    type="button" 
                    onClick={() => setEditingFromAddress(!editingFromAddress)}
                    style={{ padding: '0.5rem', fontSize: '0.9rem' }}
                  >
                    {editingFromAddress ? 'Done Editing' : 'Edit Address'}
                  </Button>
                </div>
                <Grid>
                  <FormGroup>
                    <Label htmlFor="fromName">Name / Company</Label>
                    <Input
                      type="text"
                      id="fromName"
                      name="name"
                      value={fromAddress.name}
                      onChange={handleFromAddressChange}
                      disabled={!editingFromAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="fromStreet1">Street Address</Label>
                    <Input
                      type="text"
                      id="fromStreet1"
                      name="street1"
                      value={fromAddress.street1}
                      onChange={handleFromAddressChange}
                      disabled={!editingFromAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="fromCity">City</Label>
                    <Input
                      type="text"
                      id="fromCity"
                      name="city"
                      value={fromAddress.city}
                      onChange={handleFromAddressChange}
                      disabled={!editingFromAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="fromState">State</Label>
                    <Input
                      type="text"
                      id="fromState"
                      name="state"
                      value={fromAddress.state}
                      onChange={handleFromAddressChange}
                      disabled={!editingFromAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="fromZip">ZIP Code</Label>
                    <Input
                      type="text"
                      id="fromZip"
                      name="zip"
                      value={fromAddress.zip}
                      onChange={handleFromAddressChange}
                      disabled={!editingFromAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="fromPhone">Phone</Label>
                    <Input
                      type="text"
                      id="fromPhone"
                      name="phone"
                      value={fromAddress.phone}
                      onChange={handleFromAddressChange}
                      disabled={!editingFromAddress}
                    />
                  </FormGroup>
                </Grid>
              </Section>
              
              <Section>
                <SectionTitle>Recipient Address</SectionTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div>Where the package will be delivered</div>
                  <Button 
                    type="button" 
                    onClick={() => setEditingToAddress(!editingToAddress)}
                    style={{ padding: '0.5rem', fontSize: '0.9rem' }}
                  >
                    {editingToAddress ? 'Done Editing' : 'Edit Address'}
                  </Button>
                </div>
                <Grid>
                  <FormGroup>
                    <Label htmlFor="toName">Name / Company</Label>
                    <Input
                      type="text"
                      id="toName"
                      name="name"
                      value={toAddress.name}
                      onChange={handleToAddressChange}
                      disabled={!editingToAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="toStreet1">Street Address</Label>
                    <Input
                      type="text"
                      id="toStreet1"
                      name="street1"
                      value={toAddress.street1}
                      onChange={handleToAddressChange}
                      disabled={!editingToAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="toCity">City</Label>
                    <Input
                      type="text"
                      id="toCity"
                      name="city"
                      value={toAddress.city}
                      onChange={handleToAddressChange}
                      disabled={!editingToAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="toState">State</Label>
                    <Input
                      type="text"
                      id="toState"
                      name="state"
                      value={toAddress.state}
                      onChange={handleToAddressChange}
                      disabled={!editingToAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="toZip">ZIP Code</Label>
                    <Input
                      type="text"
                      id="toZip"
                      name="zip"
                      value={toAddress.zip}
                      onChange={handleToAddressChange}
                      disabled={!editingToAddress}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="toPhone">Phone</Label>
                    <Input
                      type="text"
                      id="toPhone"
                      name="phone"
                      value={toAddress.phone}
                      onChange={handleToAddressChange}
                      disabled={!editingToAddress}
                    />
                  </FormGroup>
                </Grid>
              </Section>
            </Grid>
            
            <ButtonGroup>
              <SecondaryButton onClick={onClose}>
                <FaArrowLeft /> Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleGetRates}>
                <FaShippingFast /> Get Shipping Rates
              </PrimaryButton>
            </ButtonGroup>
          </>
        )}
        
        {step === 'rates' && (
          <>
            <Section>
              <SectionTitle>Available Shipping Rates</SectionTitle>
              <RatesContainer>
                {rates.map((rate, index) => (
                  <RateCard 
                    key={index}
                    selected={selectedRate && selectedRate.object_id === rate.object_id}
                    onClick={() => setSelectedRate(rate)}
                  >
                    <RateHeader>
                      <RateCarrier>{rate.provider.toUpperCase()} - {rate.servicelevel.name}</RateCarrier>
                      <RatePrice selected={selectedRate && selectedRate.object_id === rate.object_id}>
                        ${parseFloat(rate.amount).toFixed(2)}
                      </RatePrice>
                    </RateHeader>
                    <RateDetails>
                      Estimated delivery: {rate.estimated_days} days
                      {rate.duration_terms && ` (${rate.duration_terms})`}
                    </RateDetails>
                  </RateCard>
                ))}
              </RatesContainer>
            </Section>
            
            <ButtonGroup>
              <SecondaryButton onClick={() => setStep('dimensions')}>
                <FaArrowLeft /> Back to Dimensions
              </SecondaryButton>
              <PrimaryButton onClick={handleCreateLabel} disabled={!selectedRate}>
                <FaBox /> Create Label
              </PrimaryButton>
            </ButtonGroup>
          </>
        )}
        
        {step === 'label' && label && (
          <>
            <Section>
              <SectionTitle>Shipping Label Created</SectionTitle>
              
              <div style={{ marginBottom: '1rem' }}>
                <strong>Tracking Number:</strong> {label.tracking_number}
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <strong>Carrier:</strong> {selectedRate?.provider.toUpperCase()}
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <strong>Service:</strong> {selectedRate?.servicelevel.name}
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <strong>Cost:</strong> ${parseFloat(selectedRate?.amount).toFixed(2)}
              </div>
              
              {label.label_url && (
                <LabelPreview>
                  <img src={label.label_url} alt="Shipping Label" />
                </LabelPreview>
              )}
            </Section>
            
            <ButtonGroup>
              <SecondaryButton onClick={onClose}>
                <FaArrowLeft /> Close
              </SecondaryButton>
              <PrimaryButton onClick={handlePrintLabel}>
                <FaPrint /> Print Label
              </PrimaryButton>
            </ButtonGroup>
          </>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default ShippingLabelCreator;
